import React, { useState, useEffect, useMemo } from 'react';
import { domainService } from '../../services';
import { Domain } from '../../types';
import { DomainFormModal } from '../../components/admin/DomainFormModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';

export const DomainsPage: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const domainsData = await domainService.list();
      setDomains(domainsData);
    } catch (error) {
      console.error('Error loading domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (domain: Domain) => {
    const displayName = domain.display_name || domain.name;
    if (!confirm(`Supprimer le domaine "${displayName}" ?`)) return;
    try {
      await domainService.delete(domain.name);
      loadData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const columns: FilterableColumnDef<Domain>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      searchable: true,
      sortValueFn: (d) => d.name,
      render: (d) => (
        <span className="font-mono text-sm">
          {d.name}
          {!d.display_name && (
            <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">legacy</span>
          )}
        </span>
      ),
    },
    {
      key: 'display_name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (d) => d.display_name || <span className="text-gray-400">-</span>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (d) => (
        <span className="text-sm text-gray-600 max-w-xs truncate block" title={d.description || ''}>
          {d.description || <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (d) => d.is_active ? 'actif' : 'inactif',
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'inactif', label: 'Inactif' },
      ],
      render: (d) => d.is_active ? (
        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Actif</span>
      ) : (
        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Inactif</span>
      ),
    },
    {
      key: 'prompts',
      header: 'Prompts',
      align: 'center' as const,
      sortable: true,
      sortValueFn: (d) => String(d.prompts_count).padStart(6, '0'),
      render: (d) => (
        <>
          <span className={d.prompts_count > 0 ? 'text-gray-900' : 'text-gray-400'}>{d.prompts_count}</span>
          {d.active_prompt_version && <span className="ml-1 text-xs text-green-600">v{d.active_prompt_version}</span>}
        </>
      ),
    },
    {
      key: 'schemas',
      header: 'Schemas',
      align: 'center' as const,
      sortable: true,
      sortValueFn: (d) => String(d.schemas_count).padStart(6, '0'),
      render: (d) => (
        <>
          <span className={d.schemas_count > 0 ? 'text-gray-900' : 'text-gray-400'}>{d.schemas_count}</span>
          {d.active_schema_version && <span className="ml-1 text-xs text-green-600">v{d.active_schema_version}</span>}
        </>
      ),
    },
    {
      key: 'gt',
      header: 'GT',
      align: 'center' as const,
      sortable: true,
      sortValueFn: (d) => String(d.ground_truth_count).padStart(6, '0'),
      render: (d) => (
        <span className={d.ground_truth_count > 0 ? 'text-gray-900' : 'text-gray-400'}>{d.ground_truth_count}</span>
      ),
    },
    {
      key: 'created_by',
      header: 'Créé par',
      filterable: true,
      filterValueFn: (d) => d.created_by_name ? [d.created_by_name] : [],
      render: (d) => <span className="text-sm text-gray-500">{d.created_by_name || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (d) => {
        const isLegacy = !d.display_name;
        const hasResources = d.prompts_count > 0 || d.schemas_count > 0 || d.ground_truth_count > 0;
        return (
          <div className="flex gap-2">
            {!isLegacy ? (
              <>
                <button onClick={() => { setSelectedDomain(d); setShowForm(true); }} className="text-blue-600 hover:underline text-sm">
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(d)}
                  disabled={hasResources}
                  className={`text-sm ${hasResources ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:underline'}`}
                  title={hasResources ? 'Impossible de supprimer: ressources associées' : 'Supprimer'}
                >
                  Supprimer
                </button>
              </>
            ) : (
              <span className="text-gray-400 text-sm">Créer dans la table domains</span>
            )}
          </div>
        );
      },
    },
  ], []);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Domaines</h1>
        <button
          onClick={() => { setSelectedDomain(null); setShowForm(true); }}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouveau Domaine
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-domains"
          paginated
          data={domains}
          columns={columns}
          rowKey={(d) => d.name}
          defaultSort={{ key: 'code', direction: 'asc' }}
          rowClassName={(d) => !d.is_active ? 'text-gray-400' : ''}
          noDataMessage="Aucun domaine trouvé"
          emptyMessage="Aucun domaine ne correspond aux filtres"
        />
      )}

      {showForm && (
        <DomainFormModal
          domain={selectedDomain}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); loadData(); }}
        />
      )}
    </PageContainer>
  );
};

export default DomainsPage;
